import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, Field, NumberField, StatusPill, Toggle, type DataListColumn } from "@ozpanel/kit";
import { NodeIdentity } from "./NodeIdentity";
import { TuningOps } from "../nodes/TuningScreen";
import { TasksOps } from "../PlatformScreen";
import { RepmgrdOps } from "./RepmgrdOps";
import { GarageOps, JuiceFSOps } from "./StorageOps";
import { SyncReplicationPanel } from "../nodes/SyncReplication";
import { CaddyOps } from "./CaddyOps";
import { CrowdSecOps } from "./CrowdSecOps";
import { ExposureOps } from "./ExposureOps";
import { AccessTokensOps } from "./AccessTokensOps";
import { CertImport } from "./CertImport";
import { MariadbCluster, RedisTopology, GlusterHealth, PostgresCluster, PgbouncerPools, CaddyTls } from "./clusterops";
import { ClusterOps } from "../nodes/ClusterScreen";
import { DatabaseAdmin } from "./DatabaseAdmin";
import { MailAdmin } from "../mail/MailAdminScreen";
import { AlertReading, type AlertWire } from "../alertState";
import { Button, ConfirmDialog, Eyebrow, Meter, Panel, Section, Stat, StateView, formatBytes, useAuth, useToast } from "@ozpanel/ui";

// operations.tsx — the OPERATIONAL half of a module: what it is doing right now, and the
// actions you take against it. An audit of the OpenAPI registry (1000 documented operations
// across 59 modules) found the console reaching roughly 70 of them: every module publishes a
// /status, postfix publishes its queue and queue controls, rspamd publishes scan statistics
// and a per-message history, and none of it had ever been rendered.

// ── Generic status, for any module that publishes /{module}/status ─────────────────────────
const HIDE = new Set(["config_id", "chunks_allocated", "chunks_freed", "pools_allocated", "pools_freed", "fragmented", "bytes_allocated", "chunks_oversized", "scan_times", "fuzzy_hashes"]);

// ASKED ONLY OF A MODULE THAT DECLARES IT. The panel used to probe /{x}/status on every module and read
// any failure as "no status": most do not serve it, so every visit fired requests built to fail — and a
// module whose routes include GET /{x}/{name} answered with an OBJECT of that name (/secrets/status would
// print a secret called "status"). The module detail's has_status flag is the server's own answer to
// "does this module serve the status surface", so the caller renders this only when it is true — and a
// failure is then a real failure, said as one.
export function StatusPanel({ module }: { module: string }) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["module-status", module],
    queryFn: () => client.apiFetch<Record<string, unknown>>(`/${module}/status`),
    refetchInterval: 15000,
    retry: false,
  });
  if (q.isError) {
    return (
      <Section title="Status">
        <StateView error={q.error} errorContext={`${module}'s status`} onRetry={() => void q.refetch()}>
          {null}
        </StateView>
      </Section>
    );
  }

  const entries = Object.entries(q.data ?? {}).filter(
    ([k, v]) => !HIDE.has(k) && v !== null && typeof v !== "object",
  );
  const nested = Object.entries(q.data ?? {}).filter(
    ([k, v]) => !HIDE.has(k) && v !== null && typeof v === "object" && Object.keys(v as object).length > 0,
  );

  return (
    <Section title="Status" bodyClassName="p-0">
      <StateView loading={q.isLoading} skeleton={<div className="h-20 animate-pulse bg-surface2" />}>
        <div className="divide-y divide-line">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-baseline justify-between gap-4 px-4 py-2">
              <span className="font-mono text-[12px] text-muted">{k}</span>
              <span className="text-right text-[13px]">
                {typeof v === "boolean" ? (
                  <StatusPill tone={v ? "ok" : "neutral"}>{v ? "yes" : "no"}</StatusPill>
                ) : (
                  <span className="font-mono tabular">{String(v)}</span>
                )}
              </span>
            </div>
          ))}
          {nested.map(([k, v]) => (
            <div key={k} className="px-4 py-2.5">
              <Eyebrow className="mb-1.5">{k.replace(/_/g, " ")}</Eyebrow>
              {/* An ARRAY OF OBJECTS is rendered as rows, not stringified.
                  String({...}) is "[object Object]", and this panel is generic — so every module
                  publishing a list of records printed that. repmgrd showed "nodes 0 [object Object]
                  1 [object Object]" for the two-node cluster, which is worse than showing nothing:
                  the label promises the data is there and the value proves nothing is wrong with
                  the fetch, so it reads as a rendering quirk rather than a screen that cannot show
                  its own subject. */}
              {Array.isArray(v) ? (
                <div className="space-y-1">
                  {(v as unknown[]).map((row, i) =>
                    row !== null && typeof row === "object" ? (
                      <div key={i} className="flex flex-wrap gap-1.5">
                        {Object.entries(row as Record<string, unknown>).map(([kk, vv]) => (
                          <span key={kk} className="rounded-md border border-line bg-bg2 px-2 py-1 font-mono text-[11.5px]">
                            {kk} <span className="text-accent">{scalar(vv)}</span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span key={i} className="rounded-md border border-line bg-bg2 px-2 py-1 font-mono text-[11.5px]">
                        {scalar(row)}
                      </span>
                    ),
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(v as Record<string, unknown>).map(([kk, vv]) => (
                    <span key={kk} className="rounded-md border border-line bg-bg2 px-2 py-1 font-mono text-[11.5px]">
                      {kk} <span className="text-accent">{scalar(vv)}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </StateView>
    </Section>
  );
}

/** A value rendered for a human, never "[object Object]".
 *
 *  A nested object or array one level deeper is shown as compact JSON: it is not pretty, and it is
 *  TRUE — which is the property that matters on a generic panel that cannot know what it is
 *  displaying. Silently printing [object Object] loses the data while looking like a render. */
function scalar(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

// ── Postfix: the mail queue and its controls ──────────────────────────────────────────────

// GET /postfix/relay/lock. Field names are the SERVED JSON keys — a mismatch here renders the
// section with correct headings and empty values, which reads as "nothing is wrong" rather
// than "this is the wrong shape" (CLAUDE.md, the JSON-tag trap).
interface RelayGate {
  key: string;
  value: string;
  ok: boolean;
  want: string;
  problem?: string;
}
interface RelayLockState {
  state: "locked" | "open" | "unknown";
  locked: boolean;
  gates: RelayGate[] | null;
  blockers: string[] | null;
  measured: string;
  proof: string;
}

interface QueueItem {
  id?: string;
  queue_id?: string;
  sender?: string;
  recipients?: string[];
  size?: number;
  arrival_time?: string;
  reason?: string;
  queue?: string;
}

export function PostfixOps() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [lockResult, setLockResult] = React.useState<unknown>(null);

  // GET /postfix/relay/lock — the STATE. Without this the button rendered identically on a
  // locked node and a wide-open one, so the page offered an action and could not say whether
  // it was already in force (CLAUDE.md §1 interlock).
  const relay = useQuery({
    queryKey: ["postfix-relay-lock"],
    queryFn: () => client.apiFetch<RelayLockState>("/postfix/relay/lock"),
  });

  // POST /postfix/relay/lock — closes BOTH permit_mynetworks and the sendmail(1) bypass, then
  // PROVES relaying is refused while the MX still accepts inbound. It had no UI, which made a
  // security operation curl-only. Danger high, so it confirms.
  const lock = useMutation({
    mutationFn: () => client.apiFetch<unknown>("/postfix/relay/lock", { method: "POST" }),
    onSuccess: (r) => {
      setLockResult(r);
      notify({ kind: "success", title: "Relay policy locked", body: "Outbound now requires authentication; the result below is the server's own proof." });
      void qc.invalidateQueries({ queryKey: ["postfix-relay-lock"] });
      void qc.invalidateQueries({ queryKey: ["module-settings", "postfix"] });
      void qc.invalidateQueries({ queryKey: ["config-check"] });
    },
    onError: (e) => notify({ kind: "error", title: "Relay lock failed", body: e instanceof Error ? e.message : String(e) }),
  });
  const [confirmLock, setConfirmLock] = React.useState(false);
  const relayState = relay.data?.state;

  // One message, dropped from the queue. Deleting a single stuck message is the ordinary repair —
  // "delete all" was the only thing offered, which is the wrong tool for one bad recipient.
  const [dropping, setDropping] = React.useState<QueueItem | null>(null);
  const queue = useQuery({
    queryKey: ["postfix-queue"],
    queryFn: () => client.apiFetch<{ queue: QueueItem[] | null; total: number }>("/postfix/queue"),
    refetchInterval: 15000,
  });

  const act = useMutation({
    // The call is written where it is pressed, so its method and path are read there
    // (internal/console: a path handed to a wrapper is invisible to the reach check).
    mutationFn: ({ call, label }: { call: () => Promise<unknown>; label: string }) => call().then(() => label),
    onSuccess: (label) => {
      notify({ kind: "success", title: String(label) });
      void qc.invalidateQueries({ queryKey: ["postfix-queue"] });
      void qc.invalidateQueries({ queryKey: ["module-status", "postfix"] });
    },
    onError: (e) => notify({ kind: "error", title: "Queue action failed", body: e instanceof Error ? e.message : String(e) }),
  });

  const items = queue.data?.queue ?? [];
  const columns: DataListColumn<QueueItem>[] = [
    { key: "id", label: "Queue ID", sortable: true, value: (m) => m.queue_id || m.id || "" },
    { key: "queue", label: "Queue", sortable: true, value: (m) => m.queue || "" },
    { key: "sender", label: "Sender", sortable: true, value: (m) => m.sender || "" },
    { key: "recipients", label: "Recipients", value: (m) => (m.recipients ?? []).join(", ") },
    { key: "size", label: "Size", sortable: true, value: (m) => m.size ?? 0, render: (m) => formatBytes(m.size) },
    { key: "reason", label: "Held because", value: (m) => m.reason || "" },
  ];

  return (
    <>
    <Section
      title="Outbound relay policy"
      collapsible
      defaultOpen={false}
      id="postfix.relaylock"
      hint={
        relay.isLoading ? "reading…"
          : relayState === "locked" ? "authentication required"
          : relayState === "open" ? "NOT enforced"
          : "state unknown"
      }
      actions={
        <Button size="sm" variant={relayState === "open" ? "danger" : "ghost"}
          disabled={lock.isPending || relay.isLoading} onClick={() => setConfirmLock(true)}>
          {lock.isPending ? "Applying…" : relayState === "locked" ? "Re-check & prove" : "Lock relay"}
        </Button>
      }
    >
      {/* The state, before the description. An operator asking "is this on?" must not have to
          infer it from a button that looks the same either way. */}
      <div className="mb-3 flex items-center gap-2 text-[12.5px]">
        <span
          className={
            "inline-flex items-center rounded-full px-2 py-0.5 font-medium " +
            (relayState === "locked" ? "bg-oksoft text-ok"
              : relayState === "open" ? "bg-critsoft text-crit"
              : "bg-bg2 text-muted")
          }
        >
          {relay.isLoading ? "Reading…"
            : relayState === "locked" ? "Locked — every outbound path requires authentication"
            : relayState === "open" ? "Open — outbound can be sent without a password"
            : "Unknown — the policy could not be read"}
        </span>
      </div>

      {/* Every gate, with its live value and the consequence when it does not hold. A single
          red badge tells an operator something is wrong; this tells them which door is open. */}
      {relay.data?.gates && relay.data.gates.length > 0 && (
        <div className="mb-3 overflow-hidden rounded-lg border border-line">
          {relay.data.gates.map((g) => (
            <div key={g.key} className="flex flex-col gap-1 border-b border-line px-3 py-2 last:border-b-0">
              <div className="flex items-baseline gap-2">
                <span className={"font-mono text-[11.5px] " + (g.ok ? "text-ok" : "text-crit")}>
                  {g.ok ? "✓" : "✕"}
                </span>
                <code className="font-mono text-[11.5px] text-fg">{g.key}</code>
                <code className="font-mono text-[11.5px] text-muted break-all">= {g.value || "(empty)"}</code>
              </div>
              {!g.ok && g.problem && (
                <p className="pl-5 text-[11.5px] leading-relaxed text-crit">{g.problem}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="text-[12.5px] leading-relaxed text-muted">
        Requires authentication for EVERY outbound message — removes <code className="font-mono">permit_mynetworks</code>{" "}
        from the restriction lists and closes the <code className="font-mono">sendmail(1)</code> bypass, then proves
        relaying is refused while the MX still accepts inbound mail. Safe to re-run; it reports
        what it changed and what it verified.
      </p>
      {/* Say what the badge is NOT, so a green state is not mistaken for the behavioural proof. */}
      {relay.data?.measured && (
        <p className="mt-2 text-[11.5px] leading-relaxed text-muted">
          The state above is read from {relay.data.measured}. It is not the behavioural proof —
          running the action attempts a real unauthenticated relay, confirms inbound to a hosted
          domain still works, and tries a <code className="font-mono">sendmail(1)</code> injection
          as an unprivileged user.
        </p>
      )}
      {lockResult !== null && (
        <pre className="mt-3 max-h-64 overflow-auto rounded-lg border border-line bg-bg2 p-3 font-mono text-[11.5px] whitespace-pre-wrap text-muted">
          {JSON.stringify(lockResult, null, 2)}
        </pre>
      )}
      <ConfirmDialog
        open={confirmLock}
        onOpenChange={setConfirmLock}
        title="Require authentication for all outbound mail?"
        destructive
        confirmLabel="Lock relay policy"
        pending={lock.isPending}
        onConfirm={() => { setConfirmLock(false); lock.mutate(); }}
        body={
          <p>
            Anything that currently relays without a password stops working — a local script, a
            cron job, an application sending through localhost. Inbound mail to hosted domains is
            unaffected. Verify those senders authenticate before applying.
          </p>
        }
      />
    </Section>

    <Section
      title="Mail queue"
      actions={
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" disabled={act.isPending}
            onClick={() => act.mutate({ call: () => client.apiFetch("/postfix/queue/flush", { method: "POST" }), label: "Queue flushed — delivery retried now" })}>
            Flush
          </Button>
          {items.length > 0 && (
            <Button size="sm" variant="danger" disabled={act.isPending}
              onClick={() => act.mutate({ call: () => client.apiFetch("/postfix/queue/delete-all", { method: "POST" }), label: "Queue emptied" })}>
              Delete all
            </Button>
          )}
        </div>
      }
      bodyClassName="p-0"
    >
      <ConfirmDialog
        open={!!dropping}
        onOpenChange={(o) => !o && setDropping(null)}
        title="Delete this message from the queue?"
        confirmLabel="Delete it"
        destructive
        body={
          <p>
            The message is discarded and never delivered. The sender is NOT told — a queue deletion
            produces no bounce, so as far as they are concerned it was accepted and vanished.
          </p>
        }
        onConfirm={() => {
          const m = dropping;
          setDropping(null);
          if (!m) return;
          const qid = String(m.queue_id || m.id || "");
          if (!qid) return;
          void client
            .apiFetch(`/postfix/queue/${encodeURIComponent(qid)}/delete`, { method: "POST" })
            .then(() => {
              notify({ kind: "success", title: "Message deleted" });
              void qc.invalidateQueries({ queryKey: ["postfix-queue"] });
            })
            .catch((e: unknown) => notify({ kind: "error", title: "Could not delete it", body: e instanceof Error ? e.message : String(e) }));
        }}
      />

      <div className="p-4">
        <DataList
          items={items}
          columns={columns}
          getRowKey={(m) => String(m.queue_id || m.id)}
          loading={queue.isLoading}
          error={queue.error}
          errorContext="the mail queue"
          emptyMessage="The queue is empty."
          emptyHint="Nothing is waiting for delivery — mail is going out as fast as it arrives."
          rowActions={(m: QueueItem) => (
            <Button size="sm" variant="danger" onClick={() => setDropping(m)}>Delete</Button>
          )}
        />
      </div>
    </Section>
    </>
  );
}

// ── Rspamd: scan statistics and per-message history ───────────────────────────────────────
interface RspamdStats {
  actions?: Record<string, number>;
  ham_count?: number;
  spam_count?: number;
  learned?: number;
  connections?: number;
  scanned?: number;
  /** Seconds since the rspamd process started — the window every counter here is measured over. */
  uptime?: number;
}
interface HistoryItem {
  time: number;
  action: string;
  score: number;
  required_score: number;
  sender: string;
  recipients?: string[];
  subject: string;
  size_bytes: number;
  symbols?: string[];
}

const actionTone = (a: string) =>
  a === "reject" ? "bad" : a === "add header" || a === "rewrite subject" || a === "greylist" || a === "soft reject" ? "warn" : "ok";

export function RspamdOps() {
  const { client } = useAuth();
  const { notify } = useToast();

  const stats = useQuery({
    queryKey: ["rspamd-stats"],
    queryFn: () => client.apiFetch<RspamdStats>("/rspamd/stats"),
    refetchInterval: 20000,
    retry: false,
  });
  const history = useQuery({
    queryKey: ["rspamd-history"],
    queryFn: () => client.apiFetch<{ items: HistoryItem[]; content_note?: string }>("/rspamd/history"),
    retry: false,
  });
  // The rate limits: what is configured cluster-wide (the replicated store) next to what THIS node's
  // Rspamd actually resolved — configured vs resolved, so store/daemon drift is visible (D6).
  const rl = useQuery({
    queryKey: ["rspamd-ratelimit"],
    queryFn: () =>
      client.apiFetch<{
        buckets: { key: string; name: string; rate: string }[] | null;
        stored?: Record<string, string>;
        stored_max?: number;
        max_rcpt?: number;
        state?: string;
        note?: string;
      }>("/rspamd/ratelimit"),
    retry: false,
  });
  const selftest = useMutation({
    mutationFn: () => client.apiFetch<Record<string, unknown>>("/rspamd/engine/selftest", { method: "POST" }),
    onSuccess: (r) =>
      notify({ kind: "success", title: "Self-test complete", body: JSON.stringify(r).slice(0, 180) }),
    onError: (e) => notify({ kind: "error", title: "Self-test failed", body: e instanceof Error ? e.message : String(e) }),
  });

  const actions = stats.data?.actions ?? {};
  const total = Object.values(actions).reduce((a, b) => a + b, 0);

  const columns: DataListColumn<HistoryItem>[] = [
    {
      key: "time",
      label: "When",
      sortable: true,
      value: (h) => h.time,
      render: (h) => <span className="font-mono text-[12px]">{new Date(h.time * 1000).toLocaleString()}</span>,
    },
    {
      key: "action",
      label: "Action",
      sortable: true,
      render: (h) => <StatusPill tone={actionTone(h.action)}>{h.action}</StatusPill>,
    },
    {
      key: "score",
      label: "Score",
      sortable: true,
      value: (h) => h.score,
      // A score means nothing without the threshold it was judged against — the Interlock
      // rule applied to a number.
      render: (h) => (
        <span className="font-mono tabular">
          {h.score.toFixed(1)}
          <span className="text-faint"> / {h.required_score}</span>
        </span>
      ),
    },
    { key: "sender", label: "Sender", sortable: true },
    { key: "subject", label: "Subject" },
    {
      key: "symbols",
      label: "Matched rules",
      value: (h) => (h.symbols ?? []).join(" "),
      render: (h) => (
        <span className="font-mono text-[11px] text-muted">{(h.symbols ?? []).slice(0, 3).join(", ") || "—"}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Section
        title="Scan outcomes"
        actions={
          <Button size="sm" variant="ghost" disabled={selftest.isPending} onClick={() => selftest.mutate()}>
            {selftest.isPending ? "Testing…" : "Run self-test"}
          </Button>
        }
      >
        <StateView
          loading={stats.isLoading}
          error={stats.isError ? stats.error : undefined}
          errorContext="scan statistics"
          skeleton={<div className="h-24 animate-pulse rounded bg-surface2" />}
        >
          {total === 0 ? (
            <p className="text-[13px] text-muted">No messages scanned since Rspamd last started.</p>
          ) : (
            <div className="space-y-2.5">
              {/* THE WINDOW, stated before the numbers. These counters are cumulative since the
                  rspamd PROCESS started and reset when it restarts — they are not "today", and a
                  panel that renders them beside a date picker or a daily figure reads as if they
                  were. A reject count that looks reassuringly low can simply mean rspamd was
                  restarted an hour ago. The real per-day series is the mail flow history. */}
              <p className="text-[11.5px] leading-relaxed text-muted">
                Counted since Rspamd last started
                {stats.data?.uptime ? ` — about ${Math.round(stats.data.uptime / 3600)}h ago` : ""}, not since midnight. A restart
                resets them. For a day-by-day view use the mail flow history.
              </p>
              {Object.entries(actions)
                .sort((a, b) => b[1] - a[1])
                .map(([action, n]) => (
                  <div key={action}>
                    <div className="mb-1 flex items-baseline justify-between gap-3">
                      <span className="text-[13px]">{action}</span>
                      <span className="font-mono text-[12.5px] tabular">
                        {n}
                        <span className="text-faint"> · {total ? Math.round((n / total) * 100) : 0}%</span>
                      </span>
                    </div>
                    <Meter
                      pct={total ? (n / total) * 100 : 0}
                      tone={action === "reject" ? "crit" : action === "no action" ? undefined : "warn"}
                    />
                  </div>
                ))}
              <div className="flex flex-wrap gap-1.5 pt-1">
                {stats.data?.ham_count !== undefined && (
                  <Fact k="ham" v={stats.data.ham_count} />
                )}
                {stats.data?.spam_count !== undefined && <Fact k="spam" v={stats.data.spam_count} />}
                {stats.data?.learned !== undefined && <Fact k="learned" v={stats.data.learned} />}
                {stats.data?.connections !== undefined && <Fact k="connections" v={stats.data.connections} />}
              </div>
            </div>
          )}
        </StateView>
      </Section>

      <Section title="Rate limits" hint={rl.data?.state}>
        <StateView loading={rl.isLoading} error={rl.isError ? rl.error : undefined} errorContext="rate limits">
          <div className="space-y-1.5">
            <p className="text-[11.5px] leading-relaxed text-muted">
              Configured is the cluster-wide limit; in force is what this node's Rspamd resolved. A limit set
              on any node applies to all. {rl.data?.note}
            </p>
            {(rl.data?.buckets ?? []).map((b) => {
              const configured = rl.data?.stored?.[b.key] || "not limited";
              const live = b.rate || "not limited";
              const drift = b.rate !== "" && (rl.data?.stored?.[b.key] || "") !== "" &&
                (rl.data?.stored?.[b.key] || "").replace(/\s/g, "") !== b.rate.replace(/\s/g, "");
              return (
                <div key={b.key} className="flex items-baseline justify-between gap-3 border-b border-line py-1">
                  <span className="text-[13px]">{b.name}</span>
                  <span className="font-mono text-[12px] tabular">
                    configured {configured} · in force <span className={drift ? "text-warn" : "text-muted"}>{live}</span>
                  </span>
                </div>
              );
            })}
            <div className="pt-1 text-[11.5px] text-muted">
              Recipients per message: configured {rl.data?.stored_max ?? "—"} · in force {rl.data?.max_rcpt ?? "—"}
            </div>
          </div>
        </StateView>
      </Section>

      <Section title="Recent scans" bodyClassName="p-0">
        <div className="p-4">
          {history.data?.content_note && (
            <p className="mb-3 text-[12px] leading-relaxed text-muted">{history.data.content_note}</p>
          )}
          <DataList
            items={history.data?.items ?? []}
            columns={columns}
            getRowKey={(h) => `${h.time}-${h.subject}`}
            searchable
            searchPlaceholder="Search senders, subjects, rules…"
            loading={history.isLoading}
            error={history.isError ? history.error : undefined}
            errorContext="scan history"
            emptyMessage="No scans recorded yet."
          />
        </div>
      </Section>
    </div>
  );
}

function Fact({ k, v }: { k: string; v: number }) {
  return (
    <span className="rounded-md border border-line bg-bg2 px-2 py-1 font-mono text-[11.5px]">
      {k} <span className="text-accent tabular">{v}</span>
    </span>
  );
}

interface Protection {
  key: string;
  name: string;
  status: string;
  configured: boolean;
  working: boolean;
  detail?: string;
  why?: string;
}

/** The spam engine's protections, and whether each is actually DOING anything.
 *
 * `configured` and `working` are separate fields in the API for a reason, and this is the whole
 * value of the panel: rspamd will load a module, report itself present, and contribute nothing —
 * an antivirus with no scanner attached, a ratelimit with no rates block, DMARC reporting with no
 * cron to send them. A single "enabled" badge is exactly how that stays invisible. */
interface EngineSettings {
  antivirus?: boolean;
  antivirus_sock?: string;
  antivirus_reject?: boolean;
  phishing?: boolean;
  dmarc_reports?: boolean;
  dmarc_email?: string;
  dmarc_domain?: string;
  dmarc_org?: string;
  bayes_autolearn?: boolean;
  bayes_min_learns?: number;
  bayes_per_user?: boolean;
  bayes_per_tenant?: boolean;
  greylist?: boolean;
  ratelimit?: boolean;
}

/** The engine's settings, and the interlocks that decide whether switching one on does anything.
 *
 * Each dependent field is rendered NEXT TO the toggle that needs it, and only when it is on,
 * because this module is CLAUDE.md's own catalogue of settings that report themselves active
 * while doing nothing:
 *   - antivirus with no scanner attached: "the antivirus module loads with no scanner attached
 *     and reports itself present";
 *   - DMARC reporting with no cron: "records forever, sends nothing" — the server refuses a
 *     reporting address that is empty precisely so this cannot be saved into that state;
 *   - ratelimit with no rates block, which is the first example in the standing rules.
 * A flat list of thirteen switches would let an operator produce every one of those. */
function EngineSettingsForm({ settings, onSaved }: { settings?: EngineSettings; onSaved: () => void }) {
  const { client } = useAuth();
  const [draft, setDraft] = React.useState<EngineSettings | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const e = draft ?? settings ?? {};
  const set = <K extends keyof EngineSettings>(k: K, v: EngineSettings[K]) => setDraft({ ...e, [k]: v });

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      // The WHOLE object: handleEnginePut decodes into Engine and reconciles from it, so a
      // partial body would silently switch off everything it omitted.
      await client.apiFetch("/rspamd/engine", { method: "PUT", body: JSON.stringify(e) });
      setDraft(null);
      onSaved();
    } catch (x) {
      setErr(x instanceof Error ? x.message : String(x));
    } finally {
      setBusy(false);
    }
  };

  // Bound to the draft by key; the markup comes from the kit primitives. A local wrapper may bind
  // state, not invent a second look for an input.
  const Bool = ({ k, label, help }: { k: keyof EngineSettings; label: string; help?: string }) => (
    <Toggle checked={Boolean(e[k])} onChange={(b) => set(k, b as never)} label={label} help={help} />
  );
  const Text = ({ k, label, ph, help }: { k: keyof EngineSettings; label: string; ph?: string; help?: string }) => (
    <Field label={label} value={String(e[k] ?? "")} placeholder={ph} help={help} onChange={(v) => set(k, v as never)} />
  );

  return (
    <div className="mt-4 space-y-4 rounded-lg border border-line p-3">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted">Settings</div>

      <div className="space-y-1">
        <Bool k="antivirus" label="Scan attachments for malware" />
        {e.antivirus && (
          <div className="ml-6">
            <Text k="antivirus_sock" label="Scanner address" ph="/var/run/clamav/clamd.ctl" />
            <Bool k="antivirus_reject" label="Reject infected mail outright (otherwise it is marked)" />
            <p className="mt-1 text-[11.5px] leading-relaxed text-muted">
              Left blank the node's default socket is used. If nothing is listening there the
              module still loads and reports itself present while scanning nothing — the
              protections list above is what tells you which of the two you have.
            </p>
          </div>
        )}
      </div>

      <Bool k="phishing" label="Detect phishing links" />
      <Bool k="greylist" label="Greylist unknown senders" />
      <Bool k="ratelimit" label="Apply rate limits" />

      <div className="space-y-1">
        <Bool k="dmarc_reports" label="Send DMARC aggregate reports" />
        {e.dmarc_reports && (
          <div className="ml-6">
            <Text k="dmarc_email" label="Send reports from" ph="dmarc@example.com" />
            <Text k="dmarc_domain" label="Reporting domain" ph="example.com" />
            <Text k="dmarc_org" label="Organisation name" ph="Example Ltd" />
            <p className="mt-1 text-[11.5px] leading-relaxed text-muted">
              The address is required and the server refuses to save without one: reports cannot
              leave without a sender. Saving also installs the schedule that sends them — without
              it a node records reports for ever and sends none.
            </p>
          </div>
        )}
      </div>

      <div className="space-y-1">
        <Bool k="bayes_autolearn" label="Learn from mail automatically" />
        <Bool k="bayes_per_user" label="Keep a separate profile per mailbox" />
        <Bool k="bayes_per_tenant" label="Also learn per account (each account's own spam and ham, beside the shared profile)" />
        <NumberField
          label="Learn before scoring"
          unit="messages (0–100000)"
          value={e.bayes_min_learns ?? ""}
          onChange={(v) => set("bayes_min_learns", (v === "" ? 0 : Number(v)) as never)}
        />
      </div>

      {err && <p className="text-[12px] text-crit">{err}</p>}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => void save()} disabled={busy || draft === null}>
          {busy ? "Saving…" : "Save settings"}
        </Button>
        {draft !== null && (
          <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
            Discard
          </Button>
        )}
      </div>
      <p className="text-[11.5px] leading-relaxed text-muted">
        Saving marks these as an operator's decision, so the node's convergence stops putting its
        own defaults back over them at the next restart.
      </p>
    </div>
  );
}

function RspamdEngine() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const eng = useQuery({
    queryKey: ["rspamd-engine"],
    queryFn: () =>
      client.apiFetch<{ protections: Protection[] | null; broken: number; inactive: number; pending: boolean; pending_note?: string; note?: string; settings?: EngineSettings }>(
        "/rspamd/engine",
      ),
  });
  const bayes = useQuery({
    queryKey: ["rspamd-bayes"],
    queryFn: () =>
      client.apiFetch<{ autolearn: boolean; configured: boolean; note?: string; statfiles: { symbol: string; learned: number; users: number }[] | null }>(
        "/rspamd/bayes",
      ),
  });
  // POST /rspamd/bayes/enable was built and reached by nothing. Invalidating BOTH queries
  // afterwards: enabling the classifier changes what the engine reports too, and a panel that
  // refreshes half of itself shows two facts from two different moments.
  const enableBayes = useMutation({
    mutationFn: () => client.apiFetch<unknown>("/rspamd/bayes/enable", { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rspamd-bayes"] });
      void qc.invalidateQueries({ queryKey: ["rspamd-engine"] });
    },
  });

  const d = eng.data;

  return (
    <>
      <Section title="Protections" hint={d ? `${d.broken} not working` : undefined}>
        <StateView loading={eng.isLoading} error={eng.error} errorContext="the spam engine">
          {d && (
            <>
              {d.pending && (
                <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12.5px]">
                  {d.pending_note || "A configuration change is written but not yet in force."}
                </Panel>
              )}
              <div className="overflow-hidden rounded-lg border border-line">
                {(d.protections ?? []).map((p) => (
                  <div key={p.key} className="border-b border-line px-3 py-2 last:border-b-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[12.5px] font-medium">{p.name}</span>
                      {/* Two badges, never one. "Configured" is what the file says; "working" is
                          what the engine is doing. They disagree more often than anyone expects. */}
                      <StatusPill tone={p.configured ? "ok" : "neutral"}>
                        {p.configured ? "configured" : "not configured"}
                      </StatusPill>
                      <StatusPill tone={p.working ? "ok" : p.configured ? "bad" : "neutral"}>
                        {p.working ? "working" : "not doing anything"}
                      </StatusPill>
                      <span className="font-mono text-[11.5px] text-muted">{p.status}</span>
                    </div>
                    {(p.why || p.detail) && (
                      <p className="mt-1 text-[11.5px] leading-relaxed text-muted">{p.why || p.detail}</p>
                    )}
                  </div>
                ))}
              </div>
              {d.note && <p className="mt-3 text-[11.5px] leading-relaxed text-muted">{d.note}</p>}
              {/* The protections list above says which of these is doing nothing. Until now it
                  could only SAY it: PUT /rspamd/engine carries all thirteen settings and no
                  console sent one, so every remedy the list recommended had to be typed into a
                  file on the node by hand. */}
              <EngineSettingsForm
                settings={d.settings}
                onSaved={() => {
                  void qc.invalidateQueries({ queryKey: ["rspamd-engine"] });
                  void qc.invalidateQueries({ queryKey: ["rspamd-bayes"] });
                }}
              />
            </>
          )}
        </StateView>
      </Section>

      <Section title="Learning classifier" className="mt-6">
        <StateView loading={bayes.isLoading} error={bayes.error} errorContext="the classifier">
          {bayes.data && (
            <>
              <div className="mb-3 flex flex-wrap gap-1.5">
                <StatusPill tone={bayes.data.configured ? "ok" : "neutral"}>
                  {bayes.data.configured ? "configured" : "not configured"}
                </StatusPill>
                <StatusPill tone={bayes.data.autolearn ? "ok" : "neutral"}>
                  {bayes.data.autolearn ? "auto-learning" : "manual learning only"}
                </StatusPill>
              </div>
              <div className="flex flex-wrap gap-4">
                {(bayes.data.statfiles ?? []).map((sf) => (
                  <Stat key={sf.symbol} label={sf.symbol} value={String(sf.learned)} hint={`${sf.users} user(s)`} />
                ))}
              </div>
              {bayes.data.note && <p className="mt-3 text-[11.5px] leading-relaxed text-muted">{bayes.data.note}</p>}
              {/* The CONTROL beside the status. "not configured" was shown with no way to change
                  it, so the panel reported a state an operator could only fix by leaving it —
                  a status without its governing action, which is the interlock rule in the one
                  place this module most needed it. */}
              {!bayes.data.configured && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    disabled={enableBayes.isPending}
                    onClick={() => enableBayes.mutate()}
                  >
                    {enableBayes.isPending ? "Enabling…" : "Enable Bayes learning"}
                  </Button>
                  <span className="text-[11.5px] text-muted">
                    Stores the classifier in Redis. Auto-learning stays off — messages are learned
                    when someone marks them, not on the engine's own guess.
                  </span>
                </div>
              )}
            </>
          )}
        </StateView>
      </Section>
    </>
  );
}

/** Modules with a bespoke operational surface beyond the generic status panel. */
import { LogsPanel, SupportServicesPanel } from "../PlatformOpsScreens";
import { PostgresOps } from "./PostgresOps";
import { PodmanOps } from "./PodmanOps";
import { TincOps } from "./TincOps";
import { LicencePanel } from "../LicenceScreen";
import { UpdatesPanel } from "../UpdatesScreen";
import { DnsProviderOps, NamecheapOps } from "./DnsProviderOps";
import { HealthTerminal, PhpPoolOps, RedisAclOps, RedisFailoverOps, RedisTenantsOps } from "./SmallOps";

export const MODULE_OPS: Record<string, () => React.ReactElement> = {
  postfix: () => <PostfixOps />,
  rspamd: () => (
    <>
      <RspamdEngine />
      <RspamdOps />
    </>
  ),
  glusterfs: () => (
    <>
      <GlusterHealth />
      <GlusterVolumes />
    </>
  ),
  // The mail platform layer, under the per-domain screens: can this node send and receive, what
  // does the internet see of it, and the machinery around delivery.
  mail: () => <MailAdmin />,
  dovecot: () => (
    <>
      <DovecotSessions />
      <MailAdmin />
    </>
  ),
  hosttune: () => <TuningOps />,
  phpfpm: () => <PhpPoolOps />,
  // The container runtime dashboard — engine status, disk, and the containers running here with
  // their tenant owners. App lifecycle lives on the Apps screens; this is the engine underneath.
  podman: () => <PodmanOps />,
  // These two are MODULES, so they already have a sidebar entry of their own. A second static nav
  // item would put the same label in the same group pointing at a different route — which the nav
  // guard refuses, and rightly: the module page is where an operator already goes.
  logs: () => <LogsPanel />,
  tinc: () => <TincOps />,
  dns: () => <DnsProviderOps />,
  namecheap: () => <NamecheapOps />,
  // KEYED TO THE MODULE'S REAL NAME. This map is looked up by Manifest().Name, and the module is
  // "support-services" — keyed "supportsvc" the panel was written, reviewed and never rendered
  // once. Guarded now by TestOverviewPanelsMatchRealModules.
  "support-services": () => <SupportServicesPanel />,
  schedule: () => <TasksOps />,
  // The installation's licence (O6): what the licensing service last said, and where the key is entered.
  licence: () => <LicencePanel />,
  "release-updates": () => <UpdatesPanel />,
  // MariaDB and Galera are one system on this platform, so they render the same screen: the
  // replication facts, the recovery path, databases, access and cluster control. Reaching it
  // from either module name is deliberate — an operator looking at "galera" and one looking at
  // "mariadb" are asking the same questions.
  mariadb: () => (
    <>
      <MariadbCluster />
      <DatabaseAdmin />
    </>
  ),
  galera: () => (
    <>
      <MariadbCluster />
      <DatabaseAdmin />
    </>
  ),
  redis: () => (
    <>
      <RedisTopology />
      <RedisFailoverOps />
      <RedisAclOps />
      <RedisTenantsOps />
    </>
  ),
  // Sentinel's own module page gets the failover controls too: an operator who went looking for
  // "sentinel" during an incident should not have to know the actions live under "redis".
  // The module is "redis-sentinel"; keyed "sentinel" this never rendered either.
  "redis-sentinel": () => (
    <>
      <RedisTopology />
      <RedisFailoverOps />
    </>
  ),
  // Same pattern as mariadb: the read-only cluster table, then the actions, databases and live
  // activity beneath it.
  postgresql: () => (
    <>
      <PostgresCluster />
      <PostgresOps />
    </>
  ),
  pgbouncer: () => <PgbouncerPools />,
  // The failover daemon's page showed "healthy" and two [object Object]s while the cluster was
  // split-brained. It shows the cluster now, and carries the repair.
  repmgrd: () => (
    <>
      <RepmgrdOps />
      <SyncReplicationPanel />
    </>
  ),
  // Node identity against the cluster CA. The data and the endpoint both existed and nothing
  // rendered them, so the answer to "where do I see this" was "nowhere".
  certs: () => (
    <>
      <NodeIdentity />
      <CertImport />
    </>
  ),
  // Every access token on the platform, issued and revoked for any subject (POST /tokens, …/revoke).
  tokens: () => <AccessTokensOps />,
  // Twelve of caddy's thirteen endpoints were read by nothing. CaddyOps shows what it is doing:
  // the process and its RESTART count, every backend with its fail count, why each held
  // certificate is held, where certificates are stored, and the served-by header.
  caddy: () => (
    <>
      <CaddyOps />
      <CaddyTls />
    </>
  ),
  cluster: () => <ClusterOps />,
  // Every node's CrowdSec as it recorded itself (bans enforced in the kernel, central API accepted, the
  // node that pulls the community list, blind sources), what is blocked, and what is never blocked.
  crowdsec: () => <CrowdSecOps />,
  // The object store and the filesystem on it. Both modules mounted status + enable routes that
  // nothing in the console called, so turning them on was a curl (CLAUDE.md rule 8).
  garage: () => <GarageOps />,
  juicefs: () => <JuiceFSOps />,
  // What the public web can fetch from a hosted site that it should not. Its findings and its
  // "scan now" had no console at all.
  exposure: () => <ExposureOps />,
};

// ── Per-module cluster/live state ─────────────────────────────────────────────────────────
// "Cluster awareness for itself — its volumes, its database, its connections, sessions."
// The generic StatusPanel already surfaces the scalar cluster facts a module publishes
// (mariadb's cluster_size/threads_connected, redis's role/connected_clients). These are the
// LIST-shaped ones it cannot render: volumes with their bricks, sessions with their users.

interface Brick { name: string; host: string; path: string; status?: string; online?: boolean }
interface Volume {
  name: string; id: string; type: string; status: string; started: boolean;
  brick_count: number; replica_count: number; bricks?: Brick[];
}

export function GlusterVolumes() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["gluster-volumes"],
    queryFn: () => client.apiFetch<{ items: Volume[]; total: number }>("/glusterfs/volumes"),
    retry: false,
  });

  // A volume being MOUNTED says nothing about whether it is replicated — a 1-brick Distribute
  // volume reports fuse.glusterfs exactly like a replica-2 one, which is how "certificates
  // sync across the cluster" stayed true-looking on a single-brick volume. Replica factor is
  // therefore the headline, not a detail.
  const replicated = (v: Volume) => v.replica_count > 1;

  const columns: DataListColumn<Volume>[] = [
    { key: "name", label: "Volume", sortable: true },
    {
      key: "type",
      label: "Layout",
      sortable: true,
      value: (v) => v.type,
      render: (v) => (
        <StatusPill tone={replicated(v) ? "ok" : "warn"}>
          {v.type} {v.replica_count > 1 ? `×${v.replica_count}` : "— single copy"}
        </StatusPill>
      ),
    },
    { key: "brick_count", label: "Bricks", sortable: true, value: (v) => v.brick_count },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (v) => <StatusPill tone={v.started ? "ok" : "bad"}>{v.status}</StatusPill>,
    },
    {
      key: "hosts",
      label: "On nodes",
      value: (v) => (v.bricks ?? []).map((b) => b.host).join(", "),
      render: (v) => (
        <span className="font-mono text-[11.5px] text-muted">
          {[...new Set((v.bricks ?? []).map((b) => b.host))].join(", ") || "—"}
        </span>
      ),
    },
  ];

  const single = (q.data?.items ?? []).filter((v) => !replicated(v));

  return (
    <Section title="Volumes" bodyClassName="p-0" id="gluster.volumes" collapsible
      hint={`${q.data?.total ?? 0} volumes`}>
      <div className="p-4">
        {single.length > 0 && (
          <p className="mb-3 rounded-md border border-[color-mix(in_oklab,var(--ozp-warn)_35%,transparent)] bg-warnsoft px-3 py-2 text-[12.5px] text-warn">
            {single.map((v) => v.name).join(", ")} {single.length === 1 ? "has" : "have"} a single
            copy — the data lives on one node only. A mounted volume looks identical either way.
          </p>
        )}
        <DataList
          items={q.data?.items ?? []}
          columns={columns}
          getRowKey={(v) => v.id || v.name}
          loading={q.isLoading}
          error={q.isError ? q.error : undefined}
          errorContext="gluster volumes"
          emptyMessage="No volumes."
        />
      </div>
    </Section>
  );
}

export function DovecotSessions() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["dovecot-sessions"],
    queryFn: () => client.apiFetch<{ total: number; users: { user?: string; ip?: string; service?: string }[] | null }>("/dovecot/sessions"),
    refetchInterval: 15000,
    retry: false,
  });
  const users = q.data?.users ?? [];
  const columns: DataListColumn<{ user?: string; ip?: string; service?: string }>[] = [
    { key: "user", label: "User", sortable: true, value: (u) => u.user || "" },
    { key: "service", label: "Service", sortable: true, value: (u) => u.service || "" },
    { key: "ip", label: "From", value: (u) => u.ip || "" },
  ];
  return (
    <Section title="Connected sessions" bodyClassName="p-0" id="dovecot.sessions" collapsible
      hint={`${q.data?.total ?? 0} connected`}>
      <div className="p-4">
        <DataList
          items={users}
          columns={columns}
          getRowKey={(u, ) => `${u.user}-${u.ip}-${u.service}`}
          searchable={users.length > 8}
          loading={q.isLoading}
          error={q.isError ? q.error : undefined}
          errorContext="IMAP sessions"
          emptyMessage="Nobody is connected."
          emptyHint="No IMAP or POP session is open on this node right now."
        />
      </div>
    </Section>
  );
}

// ── What this module monitors ─────────────────────────────────────────────────────────────
type AlertRow = AlertWire;

/** MonitoredPanel shows the module's DECLARED alerts and their live readings.
 *
 * Without it the specs existed, sampled on their own cadence, and could fire — while the
 * module's own page showed no sign it was watched at all. An operator could not answer "is
 * anything monitoring this, and at what level" without reading the source. */
export function MonitoredPanel({ module: mod, alerts }: { module: string; alerts: AlertRow[] }) {
  if (!alerts?.length) return null;

  const trigger = (a: AlertRow) =>
    a.threshold ? `at or above ${a.threshold}${a.unit ? " " + a.unit : ""}`
    : a.deviation_pct ? `${a.deviation_pct}% above its own average`
    : "—";

  // The reading AND why there is none — not applicable (calm), not yet (when), cannot be measured
  // (a warning: the alert cannot fire) — from the row's own reason (alertState.tsx).
  const reading = (a: AlertRow) => <AlertReading a={a} detail />;
  // Per-instance metrics (a tinc network, a volume) share a key: the instance makes the row.
  const rowKey = (a: AlertRow) => (a.instance ? `${a.key}[${a.instance}]` : a.key);

  const columns: DataListColumn<AlertRow>[] = [
    {
      key: "label", label: "Watching", sortable: true,
      render: (a) => (
        <span>
          {a.label}
          {a.instance && <span className="ml-1.5 font-mono text-[11.5px] text-muted">{a.instance}</span>}
        </span>
      ),
    },
    { key: "reading", label: "Now", value: (a) => a.value ?? -1, render: reading },
    { key: "trigger", label: "Alerts", value: trigger, render: trigger },
    {
      key: "average",
      label: "Recent average",
      value: (a) => a.average ?? -1,
      render: (a) => (a.average !== undefined ? <span className="font-mono tabular">{a.average.toFixed(2)} <span className="text-faint">({a.samples})</span></span> : "—"),
    },
    { key: "interval_seconds", label: "Every", value: (a) => a.interval_seconds ?? 0, render: (a) => `${a.interval_seconds}s` },
  ];

  return (
    <Section title="Monitored" bodyClassName="p-0" id={`mod.monitored.${mod}`} collapsible
      hint={`${alerts.length} metric${alerts.length === 1 ? "" : "s"}`}>
      <div className="p-4">
        <p className="mb-3 text-[12.5px] leading-relaxed text-muted">
          Declared by this module and sampled on its own cadence. A breach raises an event on
          the alert channel — it does not wait for anyone to open this page.
        </p>
        <DataList items={alerts} columns={columns} getRowKey={rowKey} />
        <div className="mt-3 space-y-1.5">
          {alerts.filter((a) => a === alerts.find((b) => b.key === a.key)).map((a) => (
            <p key={rowKey(a)} className="text-[12px] leading-relaxed text-muted">
              <span className="font-mono text-faint">{a.key}</span> — {a.because}
            </p>
          ))}
        </div>
      </div>
    </Section>
  );
}
